// ============================================================================
// TournamentDetailSheet - everything about one tournament, one tap deep
// ----------------------------------------------------------------------------
// Full-screen on a phone, a tall modal on desktop. Header with the tags,
// four tabs (Details, Entries, Prize, Blinds), a big countdown, the level
// clock while it runs, and the same action button the card shows, pinned
// to the bottom so it is always reachable.
// ============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton,
  Box, HStack, VStack, Text, Button, Badge, Grid, Divider,
} from '@chakra-ui/react';
import { Trophy, Users, Coins, Clock, Layers, Medal, Info } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';
import type { Tournament } from '../../types/tournamentTypes';
import {
  ActionModel, fmtMoney, fmtClock, prizePoolOf, payoutsOf, tagsOf, statusLine, levelClock, phaseOf,
  lateRegEndMs, currencyLabel, isPlayCoinsTournament,
} from './tournamentPresenter';
import { PrimaryPill } from './TournamentListCard';

const CARD = '#0f1722';
const BORDER = 'rgba(148,163,184,0.18)';

type TabKey = 'details' | 'entries' | 'prize' | 'blinds';

export interface TournamentDetailSheetProps {
  tournament: Tournament | null;
  actions: ActionModel | null;
  isOpen: boolean;
  onClose: () => void;
  now: number;
  busy?: boolean;
  currentUserId?: string | null;
  onPrimary: () => void;
  onSecondary: (kind: ActionModel['secondary'][number]['kind']) => void;
}

const Row: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <HStack justify="space-between" py={3} borderBottom="1px solid" borderColor="whiteAlpha.100" spacing={4}>
    <HStack spacing={1.5} color="gray.400"><Text fontSize="md">{label}</Text>{hint && <Box title={hint} color="gray.500"><Info size={14} /></Box>}</HStack>
    <Text color="white" fontWeight="700" fontSize="md" textAlign="right">{value}</Text>
  </HStack>
);

const Stat: React.FC<{ icon: React.ReactNode; value: string; label: string }> = ({ icon, value, label }) => (
  <Box textAlign="center">
    <HStack justify="center" spacing={1.5}>{icon}<Text color="white" fontWeight="900" fontSize="xl" lineHeight={1.1}>{value}</Text></HStack>
    <Text color="gray.400" fontSize="sm" mt={0.5}>{label}</Text>
  </Box>
);

const TournamentDetailSheet: React.FC<TournamentDetailSheetProps> = ({ tournament: t, actions, isOpen, onClose, now, busy, currentUserId, onPrimary, onSecondary }) => {
  const [tab, setTab] = useState<TabKey>('details');
  useEffect(() => { if (isOpen) setTab('details'); }, [isOpen, t?.id]);

  const view = useMemo(() => {
    if (!t) return null;
    const phase = phaseOf(t);
    const { pool, guaranteed, entrants } = prizePoolOf(t);
    const payouts = payoutsOf(t);
    const lc = levelClock(t, now);
    const status = statusLine(t, now);
    const startMs = t.startTime instanceof Date ? t.startTime.getTime() : new Date(t.startTime as any).getTime();
    const lateEnd = lateRegEndMs(t);
    const bigCount: { label: string; value: string; tone: string } =
      phase === 'late_reg' && lateEnd && lateEnd > now ? { label: 'Late Reg Ends In', value: fmtClock(lateEnd - now, { seconds: true }), tone: POKER_COLORS.gold }
      : phase === 'live' ? { label: 'Live', value: `Level ${lc.level}`, tone: '#4ade80' }
      : phase === 'completed' ? { label: 'Finished', value: 'Results in', tone: 'gray' }
      : phase === 'cancelled' ? { label: 'Cancelled', value: 'Refunded', tone: 'gray' }
      : startMs > now ? { label: 'Starts In', value: fmtClock(startMs - now, { seconds: true }), tone: POKER_COLORS.cyan }
      : { label: 'Starting', value: 'Now', tone: POKER_COLORS.cyan };
    const startedLabel = `${phase === 'live' || phase === 'late_reg' || phase === 'completed' ? 'Started' : 'Starts'} ${new Date(startMs).toLocaleString('en-US', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`;
    return { phase, pool, guaranteed, entrants, payouts, lc, status, bigCount, startedLabel, lateEnd };
  }, [t, now]);

  if (!t || !actions || !view) return null;
  const pc = isPlayCoinsTournament(t);
  const alive = (t.activePlayers || []).length;
  const registered: string[] = t.registeredPlayers || [];
  const eliminated: string[] = Array.isArray((t as any).eliminatedPlayers) ? (t as any).eliminatedPlayers : [];
  const tables = t.tablesCount || Math.max(1, Math.ceil((alive || view.entrants) / (t.maxPlayersPerTable || 9)));
  const startingBB = t.blindLevels?.[0]?.bigBlind ? Math.round((t.startingChips || 0) / t.blindLevels[0].bigBlind) : null;

  const tabs: Array<[TabKey, string]> = [['details', 'Details'], ['entries', 'Entries'], ['prize', 'Prize'], ['blinds', 'Blinds']];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'xl' }} isCentered motionPreset="slideInBottom" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(4px)" />
      <ModalContent bg="#0a1420" color="white" borderRadius={{ base: 0, md: '2xl' }} border="1px solid" borderColor={BORDER} maxH={{ base: '100vh', md: '92vh' }} overflow="hidden">
        <ModalCloseButton mt={1} zIndex={2} />
        <ModalBody p={0} display="flex" flexDirection="column">
          {/* Header */}
          <Box px={{ base: 4, md: 6 }} pt={5} pb={3} bg={`linear-gradient(180deg, ${POKER_COLORS.gold}14 0%, transparent 100%)`}>
            <HStack align="flex-start" spacing={3} pr={8}>
              <Box w="64px" h="64px" borderRadius="2xl" bg={`${POKER_COLORS.gold}1f`} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                <Trophy size={34} color={POKER_COLORS.gold} />
              </Box>
              <Box flex={1} minW={0}>
                <Text fontSize="xl" fontWeight="900" lineHeight="1.15">{fmtMoney(t, t.buyInAmount || 0)} {t.name}</Text>
                <HStack spacing={2} mt={2} overflowX="auto" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                  {tagsOf(t).map(tag => (
                    <Badge key={tag} bg="whiteAlpha.100" color="gray.200" borderRadius="md" px={2.5} py={1} fontSize="xs" textTransform="none" flexShrink={0}>{tag}</Badge>
                  ))}
                </HStack>
              </Box>
            </HStack>
            {(t as any).hostName && <Text color="gray.400" fontSize="sm" mt={2}>Hosted by {(t as any).hostName}</Text>}
          </Box>

          {/* Tabs */}
          <HStack px={{ base: 4, md: 6 }} spacing={2} overflowX="auto" py={2} sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
            {tabs.map(([key, label]) => (
              <Button key={key} size="sm" minH="40px" px={4} borderRadius="lg" flexShrink={0} fontWeight="800"
                bg={tab === key ? POKER_COLORS.cyan : 'whiteAlpha.100'} color={tab === key ? '#0a1420' : 'gray.300'}
                _hover={{ bg: tab === key ? POKER_COLORS.cyan : 'whiteAlpha.200' }} onClick={() => setTab(key)}>
                {label}
              </Button>
            ))}
          </HStack>

          {/* Body */}
          <Box flex={1} overflowY="auto" px={{ base: 4, md: 6 }} pb={4}>
            {tab === 'details' && (
              <VStack align="stretch" spacing={3}>
                <Box bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" p={4} textAlign="center">
                  <Badge bg={view.bigCount.tone === 'gray' ? 'whiteAlpha.200' : view.bigCount.tone} color={view.bigCount.tone === 'gray' ? 'gray.200' : '#0a1420'} borderRadius="full" px={3} py={1} fontSize="xs" textTransform="none" fontWeight="800">
                    {view.bigCount.label}
                  </Badge>
                  <Text fontSize="4xl" fontWeight="900" color={view.bigCount.tone === 'gray' ? 'gray.300' : view.bigCount.tone} lineHeight={1.1} mt={2} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {view.bigCount.value}
                  </Text>
                  <Text color="gray.400" fontSize="sm" mt={2} bg="whiteAlpha.100" display="inline-block" px={3} py={1} borderRadius="full">{view.startedLabel}</Text>

                  {(view.phase === 'live' || view.phase === 'late_reg') && (
                    <Grid templateColumns="1fr 1fr 1fr" gap={2} mt={4} alignItems="center">
                      <Box>
                        <Text color="white" fontWeight="900" fontSize="lg">{view.lc.sb.toLocaleString()}/{view.lc.bb.toLocaleString()}</Text>
                        <Text color="gray.400" fontSize="xs">Level <Text as="span" color={POKER_COLORS.gold} fontWeight="800">{view.lc.level}</Text>{view.lc.ante ? ` · ante ${view.lc.ante}` : ''}</Text>
                      </Box>
                      <Box>
                        <Text color="#4ade80" fontWeight="900" fontSize="lg" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {view.lc.onBreak ? 'Break' : view.lc.msToNext != null ? fmtClock(view.lc.msToNext, { seconds: true }) : `${t.blindLevelDuration} min`}
                        </Text>
                        <Text color="gray.400" fontSize="xs">{view.lc.onBreak ? 'Clock paused' : 'Blinds up'}</Text>
                      </Box>
                      <Box>
                        <Text color="white" fontWeight="900" fontSize="lg">{view.lc.next ? `${view.lc.next.sb.toLocaleString()}/${view.lc.next.bb.toLocaleString()}` : 'Final'}</Text>
                        <Text color="gray.400" fontSize="xs">Next level</Text>
                      </Box>
                    </Grid>
                  )}

                  <Divider borderColor="whiteAlpha.200" my={4} />
                  <Grid templateColumns="1fr 1fr 1fr" gap={2}>
                    <Stat icon={<Coins size={18} color={pc ? POKER_COLORS.cyan : '#34d399'} />} value={fmtMoney(t, t.buyInAmount || 0)} label="Buy-in" />
                    <Stat icon={<Trophy size={18} color={POKER_COLORS.gold} />} value={fmtMoney(t, view.pool, { compact: true })} label={view.guaranteed ? 'Guaranteed' : 'Prize pool'} />
                    <Stat icon={<Users size={18} color="#60a5fa" />} value={String(view.entrants)} label={alive > 0 && view.phase !== 'registering' ? `${alive} left` : 'Entries'} />
                  </Grid>
                </Box>

                {actions.primary.hint && <Text color="#fca5a5" fontSize="sm" textAlign="center">{actions.primary.hint}</Text>}

                <Box bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" px={4} py={1}>
                  <Row label="Starting stack" value={`${(t.startingChips || 0).toLocaleString()}${startingBB ? ` (${startingBB} BB)` : ''}`} />
                  <Row label="Blind level" value={`${t.blindLevelDuration || 10} min`} />
                  <Row label="Late reg / re-entries" value={`End of level ${t.lateRegLevels || 4}`} hint="Register or rebuy until this level ends." />
                  <Row label="Rebuys" value={(t as any).rebuyAllowed === false ? 'None (freezeout)' : `${fmtMoney(t, t.buyInAmount || 0)} for ${(t.startingChips || 0).toLocaleString()} chips`} />
                  <Row label="Add-on" value={(t as any).addonAllowed || (t as any).addOnAllowed ? `${fmtMoney(t, (t as any).addonCost || (t as any).addOnCost || t.buyInAmount || 0)} for ${((t as any).addonChips || (t as any).addOnChips || t.startingChips || 0).toLocaleString()} chips` : 'None'} />
                  <Row label="Tables" value={`${tables} · ${t.maxPlayersPerTable || 9} seats`} />
                  <Row label="Decision timer" value={`${t.decisionTimerSeconds || 15}s + ${t.timeBankSeconds || 10}s bank`} />
                  <Row label="Paid places" value={`Top ${view.payouts.length}`} />
                  <Row label="Min to start" value={`${t.minPlayers || 5} players`} hint="Full refund if the tournament does not reach it." />
                  <Row label="Rake" value={pc ? 'None' : `${t.houseRakePercent ?? 10}% of buy-ins`} />
                </Box>
                {t.description && <Text color="gray.300" fontSize="md" px={1}>{t.description}</Text>}
              </VStack>
            )}

            {tab === 'entries' && (
              <VStack align="stretch" spacing={3}>
                <Grid templateColumns="1fr 1fr 1fr" gap={2} bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" p={4}>
                  <Stat icon={<Users size={18} color="#60a5fa" />} value={String(registered.length || view.entrants)} label="Entries" />
                  <Stat icon={<Layers size={18} color={POKER_COLORS.cyan} />} value={String(view.phase === 'registering' || view.phase === 'starting' ? registered.length || view.entrants : alive)} label="Still in" />
                  <Stat icon={<Trophy size={18} color={POKER_COLORS.gold} />} value={String(eliminated.length)} label="Busted" />
                </Grid>
                {currentUserId && registered.includes(currentUserId) && (
                  <Box bg={`${POKER_COLORS.gold}14`} border="1px solid" borderColor={`${POKER_COLORS.gold}55`} borderRadius="xl" p={3}>
                    <Text color={POKER_COLORS.gold} fontWeight="800">You are entered</Text>
                    <Text color="gray.300" fontSize="sm">{actions.isBusted ? 'You busted out.' : view.phase === 'live' || view.phase === 'late_reg' ? 'You are still in.' : 'We will seat you when it starts and bring you to your table.'}</Text>
                  </Box>
                )}
                <Text color="gray.400" fontSize="sm">Seats fill as players register. {t.minPlayers || 5} players are needed to start; everyone is refunded if it does not run.</Text>
              </VStack>
            )}

            {tab === 'prize' && (
              <VStack align="stretch" spacing={3}>
                <Box bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" p={4} textAlign="center">
                  <Text color="gray.400" fontSize="sm">{view.guaranteed ? 'Guaranteed prize pool' : 'Prize pool so far'}</Text>
                  <Text color={POKER_COLORS.gold} fontWeight="900" fontSize="3xl">{fmtMoney(t, view.pool)}</Text>
                  <Text color="gray.500" fontSize="xs">{view.entrants} entr{view.entrants === 1 ? 'y' : 'ies'} · {currencyLabel(t)}{pc ? ' · no rake' : ` · ${t.houseRakePercent ?? 10}% rake`}</Text>
                </Box>
                <Box bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" px={4} py={1}>
                  {view.payouts.map(p => (
                    <HStack key={p.position} justify="space-between" py={3} borderBottom="1px solid" borderColor="whiteAlpha.100">
                      <HStack spacing={2}>
                        <Medal size={16} color={p.position === 1 ? POKER_COLORS.gold : p.position === 2 ? '#C0C0C0' : p.position === 3 ? '#CD7F32' : '#64748b'} />
                        <Text color="white" fontWeight="700">{p.position}{p.position === 1 ? 'st' : p.position === 2 ? 'nd' : p.position === 3 ? 'rd' : 'th'}</Text>
                        <Text color="gray.500" fontSize="sm">{p.percentage}%</Text>
                      </HStack>
                      <Text color={POKER_COLORS.gold} fontWeight="800">{fmtMoney(t, p.amount)}</Text>
                    </HStack>
                  ))}
                </Box>
              </VStack>
            )}

            {tab === 'blinds' && (
              <Box bg={CARD} border="1px solid" borderColor={BORDER} borderRadius="2xl" overflow="hidden">
                <Grid templateColumns="60px 1fr 1fr 1fr" px={4} py={2} bg="whiteAlpha.100" color="gray.400" fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em">
                  <Text>Level</Text><Text>Blinds</Text><Text>Ante</Text><Text textAlign="right">Length</Text>
                </Grid>
                {(t.blindLevels || []).map((lvl, i) => {
                  const isNow = (view.phase === 'live' || view.phase === 'late_reg') && (lvl.level || i + 1) === view.lc.level;
                  const isLateEnd = (lvl.level || i + 1) === (t.lateRegLevels || 4);
                  return (
                    <Grid key={i} templateColumns="60px 1fr 1fr 1fr" px={4} py={2.5} borderTop="1px solid" borderColor="whiteAlpha.100"
                      bg={isNow ? `${POKER_COLORS.cyan}1a` : undefined} color={isNow ? 'white' : 'gray.200'} fontSize="md">
                      <Text fontWeight="800" color={isNow ? POKER_COLORS.cyan : undefined}>{lvl.level || i + 1}</Text>
                      <Text sx={{ fontVariantNumeric: 'tabular-nums' }}>{lvl.smallBlind.toLocaleString()}/{lvl.bigBlind.toLocaleString()}</Text>
                      <Text color="gray.400" sx={{ fontVariantNumeric: 'tabular-nums' }}>{lvl.ante ? lvl.ante.toLocaleString() : '-'}</Text>
                      <Text textAlign="right" color="gray.400">{(lvl as any).duration || t.blindLevelDuration || 10} min{isLateEnd ? ' · late reg closes' : ''}</Text>
                    </Grid>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Sticky action */}
          <Box px={{ base: 4, md: 6 }} py={3} borderTop="1px solid" borderColor={BORDER} bg="#0a1420" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}>
            <HStack spacing={2} mb={actions.secondary.length ? 2 : 0} align="center">
              <Box flex={1} minW={0}>
                <Text color="gray.400" fontSize="xs" noOfLines={1}>{view.status.text}</Text>
              </Box>
              <Box flex={2}><PrimaryPill actions={actions} busy={busy} onClick={onPrimary} full /></Box>
            </HStack>
            {actions.secondary.length > 0 && (
              <HStack spacing={2} flexWrap="wrap">
                {actions.secondary.map(s => (
                  <Button key={s.kind} size="sm" minH="40px" flex={1} variant="outline" borderColor="whiteAlpha.300"
                    color={s.kind === 'unregister' ? '#fca5a5' : s.kind === 'force_start' ? 'orange.300' : 'gray.200'}
                    _hover={{ bg: 'whiteAlpha.100' }} isDisabled={s.disabled} onClick={() => onSecondary(s.kind)}>
                    {s.label}
                  </Button>
                ))}
              </HStack>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TournamentDetailSheet;
